import {
  DisconnectReason,
  LocalTrackPublication,
  Participant,
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
  VideoPresets,
} from 'livekit-client';

import { Subscription } from '@libs/subscription';

import { connectRoom, exchangeTicket, participantRole } from '@features/video/lib/livekit';
import { VideoLog } from '@features/video/lib/log';
import { MicWatchdog } from '@features/video/lib/micWatchdog';
import { CleanAudio, NoiseSuppressor } from '@features/video/lib/rnnoise';
import { decodeTicket } from '@features/video/lib/ticket';
import { MicIssue, RoomStatus, VideoStreamMode } from '@features/video/types';

// Бюджет обычного прохода: штатные 15 с избыточны, а relay-проход идет с дефолтом SDK
const DIRECT_PASS_TIMEOUT_MS = 10000;

export type VideoRoomEvent =
  | { type: 'status'; status: RoomStatus }
  // комната закрыта сервером (ended) или связь потеряна (dropped)
  | { type: 'disconnected'; reason: 'ended' | 'dropped' }
  // судья выключил нам микрофон или камеру с сервера
  | { type: 'mic'; on: boolean }
  | { type: 'cam'; on: boolean }
  // микрофон после прерывания (звонок, другое приложение), см. MicIssue
  | { type: 'mic-issue'; issue: MicIssue }
  // браузер не дает играть звук судей без клика
  | { type: 'audio-blocked'; blocked: boolean }
  // трек для индикатора уровня: выход шумодава, мьют его не касается; null — шумодава нет,
  // индикатор берет захват
  | { type: 'meter'; track: MediaStreamTrack | null };

export type VideoRoomOptions = {
  // роль устройства: основная или дополнительная камера; уходит в имя треков, по нему
  // судья нумерует камеры, а не по порядку входа
  mode: VideoStreamMode;
  micOn: boolean;
  camOn: boolean;
  speakerOn: boolean;
  // журнал в Datadog с контекстом этого устройства
  log: VideoLog;
  // шумодав: хук создает и будит его в клике входа, дальше им владеет клиент
  rnnoise: NoiseSuppressor | null;
};

// Одно устройство в комнате LiveKit: подключение, публикация своих треков и
// прослушивание судей. Логика повторяет демо-клиент: autoSubscribe выключен,
// подписка только на звук участников с ролью host, зал ожидания — через права.
export class VideoRoomClient {
  readonly events = new Subscription<VideoRoomEvent>();

  private room: Room | null = null;
  private rnnoise: NoiseSuppressor | null;
  private audioEls = new Set<HTMLMediaElement>();
  private connecting = false;
  private waiting = false;
  private published = false;
  private micPub: LocalTrackPublication | null = null;
  private camPub: LocalTrackPublication | null = null;
  private speakerOn: boolean;
  // локальные треки: камера публикуется всегда, микрофон — если он есть в потоке
  private stream: MediaStream | null = null;
  // билет текущей комнаты: куда возвращаться, если переход не удался
  private ticket: string | null = null;
  // сырой трек на входе шумодава и сторож за ним (см. MicWatchdog)
  private rawMic: MediaStreamTrack | null = null;
  private watchdog: MicWatchdog;
  // контекст шумодава ждет клика: трек в эфире замьючен при включенном микрофоне
  private gestureNeeded = false;
  // мьют, который поставили мы сами: его эхо от LiveKit не трогает кнопку
  private wantMuted = false;
  // когда началось восстановление связи: длительность в журнал
  private reconnectingAt = 0;

  constructor(private opts: VideoRoomOptions) {
    this.speakerOn = opts.speakerOn;
    this.rnnoise = opts.rnnoise;
    this.watchdog = new MicWatchdog({
      rnnoise: this.rnnoise,
      raw: () => this.rawMic,
      active: () => this.micPub !== null,
      micOn: () => this.opts.micOn,
      onIssue: (issue) => {
        this.gestureNeeded = issue === 'gesture';
        this.syncMute().catch(() => {});
        this.emit({ type: 'mic-issue', issue });
      },
      // цепочка заново: тот же трек в публикации, индикатору — новый отвод
      onRebuilt: async (clean) => {
        await this.micPub?.track?.replaceTrack(clean.track);
        clean.track.enabled = this.opts.micOn;
        this.emit({ type: 'meter', track: clean.meter });
      },
      log: () => this.opts.log,
    });
    if (this.rnnoise) this.rnnoise.onState = () => this.watchdog.check();
  }

  // Разбудить аудиоконтекст: синхронно из клика, до первых await
  wake(): void {
    this.rnnoise?.wake();
  }

  async connect(ticket: string, stream: MediaStream): Promise<void> {
    const claims = decodeTicket(ticket);
    if (!claims) throw new Error('Некорректная ссылка');
    this.stream = stream;
    this.ticket = ticket;

    this.emit({ type: 'status', status: 'connecting' });
    this.rnnoise?.load().catch((e: unknown) => this.opts.log.error('rnnoise_load_failed', e));
    const { log } = this.opts;
    log.set({ server: claims.aud, room: claims.room });
    const tokenData = await exchangeTicket(claims.aud, ticket, log);

    this.connecting = true;
    try {
      this.room = await connectRoom(
        () => this.createRoom(),
        tokenData,
        DIRECT_PASS_TIMEOUT_MS,
        log,
      );
    } catch {
      this.room = null;
      throw new Error('Не удалось подключиться к серверу');
    } finally {
      this.connecting = false;
    }

    // Зал ожидания: токен без прав, ждем, пока судья откроет права
    if (tokenData.waiting) {
      this.waiting = true;
      log.event('waiting');
      this.emit({ type: 'status', status: 'waiting' });
      return;
    }
    try {
      await this.publish();
    } catch (e) {
      log.error('publish_failed', e);
      throw e;
    }
  }

  async setMicOn(on: boolean): Promise<void> {
    this.opts.micOn = on;
    if (!this.micPub) return;
    // включение — жест: будим контекст шумодава, если он уснул после прерывания
    if (on) this.rnnoise?.wake();
    await this.syncMute();
    if (on) this.watchdog.check();
  }

  async setCamOn(on: boolean): Promise<void> {
    this.opts.camOn = on;
    if (!this.camPub) return;
    await (on ? this.camPub.unmute() : this.camPub.mute());
  }

  setSpeakerOn(on: boolean): void {
    this.speakerOn = on;
    this.audioEls.forEach((el) => {
      el.muted = !on;
    });
  }

  get isConnected(): boolean {
    return this.room !== null;
  }

  // Переход в другую комнату с теми же треками: устройства не перезапрашиваем,
  // шумодав живет дальше, мьют от судьи (opts) переживает переход. Не вошли —
  // возвращаемся в прежнюю комнату; не вышло и это — ошибка
  async switchRoom(ticket: string): Promise<void> {
    const stream = this.stream;
    const previous = this.ticket;
    if (!stream || !previous) throw new Error('Нет подключения');
    if (ticket === previous) return;
    const to = decodeTicket(ticket)?.room;
    this.leaveRoom();
    try {
      await this.connect(ticket, stream);
      this.opts.log.event('switch_room', { to, outcome: 'switched' });
    } catch {
      this.leaveRoom();
      await this.connect(previous, stream);
      this.opts.log.event('switch_room', { to, outcome: 'fallback' });
    }
  }

  // Новые треки от хука устройств (смена устройства, возврат захвата). Старые он остановил,
  // поэтому публикуем заново, а не подменяем: мьют возьмет publish из opts. В зале ожидания
  // публикации нет — новый поток уйдет, когда судья впустит
  async replaceTracks(stream: MediaStream): Promise<void> {
    this.stream = stream;
    const room = this.room;
    if (!room || !this.published) return;
    this.watchdog.stop();
    this.rawMic = null;
    for (const pub of [this.camPub, this.micPub]) {
      if (pub?.track) await room.localParticipant.unpublishTrack(pub.track, true);
    }
    this.camPub = null;
    this.micPub = null;
    this.published = false;
    await this.publish();
    this.opts.log.event('tracks_replaced');
  }

  // Клик по «Включить микрофон»: будим контекст синхронно, пока жив жест, и проверяем
  wakeMic(): void {
    this.watchdog.wake();
  }

  // Клик по плашке: включить звук судей, который браузер не дал запустить сам
  startAudio(): void {
    this.room?.startAudio().catch(() => {});
  }

  disconnect(): void {
    this.leaveRoom();
    this.rnnoise?.destroy();
  }

  /*
      ____,-------------------------------,____
      \   |            Private            |   /
      /___|-------------------------------|___\
  */

  private leaveRoom(): void {
    this.connecting = false;
    this.waiting = false;
    this.published = false;
    this.micPub = null;
    this.camPub = null;
    this.watchdog.stop();
    this.rawMic = null;
    this.gestureNeeded = false;
    this.wantMuted = false;
    this.emit({ type: 'meter', track: null });
    this.audioEls.forEach((el) => el.remove());
    this.audioEls.clear();
    // локальные треки не трогаем: при смене комнаты они нужны дальше, а при выходе
    // их остановит хук устройств
    this.room?.disconnect(false);
    this.room = null;
  }

  private emit(event: VideoRoomEvent): void {
    this.events.publish(event);
  }

  private createRoom(): Room {
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      publishDefaults: {
        // симулькаст: верхний слой 720p при 15 кадрах, ниже 360p для плиток сетки — SFU
        // раздает нужный. H.264 кодирует железо телефона. При нехватке канала теряем кадры,
        // а не резкость: судье важна картинка, а не плавность
        simulcast: true,
        videoCodec: 'h264',
        videoEncoding: { maxBitrate: 1_200_000, maxFramerate: 15 },
        videoSimulcastLayers: [VideoPresets.h360],
        degradationPreference: 'maintain-resolution',
      },
    });

    // Судья зашел или опубликовал трек — подписываемся
    room.on(RoomEvent.ParticipantConnected, this.syncSubscriptions);
    room.on(RoomEvent.TrackPublished, this.syncSubscriptions);
    room.on(RoomEvent.ParticipantMetadataChanged, this.syncSubscriptions);

    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind !== Track.Kind.Audio) return;
      const el = track.attach();
      el.muted = !this.speakerOn;
      document.body.appendChild(el);
      this.audioEls.add(el);
    });
    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach().forEach((el) => {
        el.remove();
        this.audioEls.delete(el);
      });
    });

    // Браузер не дает играть звук без клика: плашка, а первый клик где угодно включит сам
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      const blocked = !room.canPlaybackAudio;
      if (blocked) {
        this.opts.log.event('audio_blocked');
        document.addEventListener('click', () => room.startAudio().catch(() => {}), { once: true });
      }
      this.emit({ type: 'audio-blocked', blocked });
    });

    // Права изменились: судья впустил из зала ожидания или вернул в него
    room.on(RoomEvent.ParticipantPermissionsChanged, (_prev, participant: Participant) => {
      if (participant !== room.localParticipant) return;
      const canPublish = !!participant.permissions?.canPublish;
      if (canPublish && this.waiting) {
        this.waiting = false;
        this.publish().catch((e: unknown) => {
          this.opts.log.error('publish_failed', e);
          this.emit({ type: 'disconnected', reason: 'dropped' });
        });
      } else if (!canPublish && !this.waiting) {
        this.waiting = true;
        this.emit({ type: 'status', status: 'waiting' });
      }
    });

    // Мьют с сервера: синхронизируем кнопки; эхо своего мьюта микрофона пропускаем
    room.on(RoomEvent.TrackMuted, (pub, participant) => {
      if (participant !== room.localParticipant) return;
      if (pub.source === Track.Source.Microphone && this.wantMuted) return;
      this.emitLocalMute(pub.source, false);
    });
    room.on(RoomEvent.TrackUnmuted, (pub, participant) => {
      if (participant !== room.localParticipant) return;
      if (pub.source === Track.Source.Microphone && !this.wantMuted) return;
      this.emitLocalMute(pub.source, true);
    });

    room.on(RoomEvent.Reconnecting, () => {
      this.reconnectingAt = Date.now();
      this.opts.log.event('reconnecting');
      this.emit({ type: 'status', status: 'reconnecting' });
    });
    room.on(RoomEvent.Reconnected, () => {
      this.opts.log.event('reconnected', { durationMs: Date.now() - this.reconnectingAt });
      this.syncSubscriptions();
      this.emit({ type: 'status', status: this.waiting ? 'waiting' : 'live' });
    });
    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      // провал прохода внутри connect разбирает сам цикл
      if (this.connecting || reason === DisconnectReason.CLIENT_INITIATED) return;
      const ended =
        reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.ROOM_CLOSED;
      this.opts.log.event('disconnected', { reason: disconnectReasonName(reason), ended });
      this.emit({ type: 'disconnected', reason: ended ? 'ended' : 'dropped' });
    });

    return room;
  }

  private async publish(): Promise<void> {
    const room = this.room;
    if (!room || this.published) return;
    this.published = true;

    const { camOn } = this.opts;
    const cam = this.stream?.getVideoTracks()[0];
    const rawMic = this.stream?.getAudioTracks()[0];

    if (cam) {
      // подсказка кодеку: детали важнее движения
      cam.contentHint = 'detail';
      this.camPub = await room.localParticipant.publishTrack(cam, {
        source: Track.Source.Camera,
        name: this.opts.mode,
      });
      if (!camOn) await this.camPub.mute();
    }
    if (rawMic) {
      // Чистый трек подменяет сырой до публикации: в эфир не уходит необработанный звук.
      // Без шумодава в эфир идет копия сырого: мьют глушит ее, а оригинал остается
      // индикатору уровня
      let clean: CleanAudio | null = null;
      if (this.rnnoise) {
        try {
          clean = await this.rnnoise.attach(rawMic);
        } catch {
          // шумодав не собрался: в эфир идет сырой звук
        }
      }
      const mic = clean?.track ?? rawMic.clone();
      this.micPub = await room.localParticipant.publishTrack(mic, {
        source: Track.Source.Microphone,
        name: this.opts.mode,
      });
      // контекст не запустился без клика: в эфир пока тишину не шлем, плашку покажет сторож
      this.gestureNeeded = clean !== null && this.rnnoise?.isRunning === false;
      await this.syncMute();
      this.emit({ type: 'meter', track: clean?.meter ?? null });
      this.rawMic = rawMic;
      this.watchdog.start();
    }

    this.syncSubscriptions();
    this.emit({ type: 'status', status: 'live' });
  }

  // Подписка только на звук судей (роль host в метаданных); в зале ожидания слушать нельзя
  private syncSubscriptions = (): void => {
    const room = this.room;
    if (!room || room.localParticipant.permissions?.canSubscribe === false) return;
    room.remoteParticipants.forEach((p) => {
      const isHost = participantRole(p) === 'host';
      p.trackPublications.forEach((pub) => {
        const want = isHost && pub.kind === Track.Kind.Audio;
        if (want && !pub.isSubscribed) pub.setSubscribed(true);
        else if (!want && pub.isSubscribed) pub.setSubscribed(false);
      });
    });
  };

  // Трек в эфире замьючен, пока микрофон выключен или контекст шумодава ждет клика:
  // судья видит «выключен», а не живой значок с тишиной
  private async syncMute(): Promise<void> {
    const pub = this.micPub;
    if (!pub) return;
    this.wantMuted = !this.opts.micOn || this.gestureNeeded;
    if (pub.isMuted === this.wantMuted) return;
    await (this.wantMuted ? pub.mute() : pub.unmute());
  }

  private emitLocalMute(source: Track.Source, on: boolean): void {
    if (source === Track.Source.Microphone) {
      this.opts.micOn = on;
      this.emit({ type: 'mic', on });
    } else if (source === Track.Source.Camera) {
      this.opts.camOn = on;
      this.emit({ type: 'cam', on });
    }
  }
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Имя причины отключения из числового перечисления LiveKit
function disconnectReasonName(reason?: DisconnectReason): string {
  return reason === undefined ? 'unknown' : (DisconnectReason[reason] ?? String(reason));
}
